import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import type { Vec3 } from "@models/data/entityData";
import type { IProjectModel } from "@models/project/IProjectModel";
import { KnownUnits, formatDistance } from "@models/utils/scaleUnits";
import type {
  IMeasurementAnchorHost,
  IMeasurementsModel,
  Measurement,
  MeasurementPointType,
  MeasurementToolMode,
} from "./IMeasurementsModel";

/**
 * Session-local implementation of {@link IMeasurementsModel} — see that file
 * for the scope note and the Unity mapping.
 */

/** Unity `MeasurementsLayerModel` snapback threshold for a released pill. */
const K_SNAPBACK_THRESHOLD = 0.02;
/** Unity `FinishNewMeasurement`: shorter than this and the ruler is discarded. */
const K_MIN_WORLD_LENGTH = 1e-5;

export class MeasurementsModel implements IMeasurementsModel {
  readonly onChanged = new Emitter<ReadonlyArray<Measurement>>();
  readonly onUnitChanged = new Emitter<KnownUnits>();
  readonly onModeChanged = new Emitter<MeasurementToolMode>();
  readonly onVisibilityChanged = new Emitter<boolean>();

  private m_measurements: Measurement[] = [];
  // Centimetres, matching the unit the bounding-box pills were hardcoded to
  // before this model existed — so the Dimensions readout is unchanged by
  // default and only moves when the user picks something else.
  private m_unit: KnownUnits = KnownUnits.Centimeters;
  // Unity's `MeasurementTool` field defaults to PointToPoint, but the tool
  // opens in Dimensions because that is what the web already shipped; flipping
  // the default would silently change the existing tool's behaviour.
  private m_mode: MeasurementToolMode = "boundingBox";
  private m_host: IMeasurementAnchorHost | null = null;
  private m_areMeasurementsVisible = true;
  /** Pre-drag snapshot per id, for the revert-on-empty-area path. */
  private readonly m_preEdit = new Map<string, Measurement>();
  private readonly m_offScene: () => void;
  private readonly m_offProject: () => void;

  constructor(project: IProjectModel) {
    // Unity's layer is per-scene; the web drops everything on either change
    // rather than showing a previous scene's rulers against new geometry.
    this.m_offScene = project.onActiveSceneChanged.on(() => this.clear());
    this.m_offProject = project.onActiveProjectChanged.on(() => this.clear());
  }

  get measurements(): ReadonlyArray<Measurement> {
    return this.m_measurements;
  }

  get unit(): KnownUnits {
    return this.m_unit;
  }

  set unit(value: KnownUnits) {
    if (this.m_unit === value) return;
    this.m_unit = value;
    this.onUnitChanged.emit(value);
    // Every visible readout is derived from the unit, so a change is a repaint.
    this.onChanged.emit(this.m_measurements);
  }

  get mode(): MeasurementToolMode {
    return this.m_mode;
  }

  set mode(value: MeasurementToolMode) {
    if (this.m_mode === value) return;
    this.m_mode = value;
    this.onModeChanged.emit(value);
  }

  get areMeasurementsVisible(): boolean {
    return this.m_areMeasurementsVisible;
  }

  setMeasurementsVisible(visible: boolean): void {
    if (this.m_areMeasurementsVisible === visible) return;
    this.m_areMeasurementsVisible = visible;
    this.onVisibilityChanged.emit(visible);
  }

  attachAnchorHost(host: IMeasurementAnchorHost | null): void {
    this.m_host = host;
  }

  beginNewMeasurement(snapshotId: string, worldPos: Vec3): string {
    if (!snapshotId) return "";
    const local = this.toLocal(snapshotId, worldPos);
    const id = randomUuid();
    // Unity seeds BOTH ends at the hit point; the End is what the drag moves.
    this.m_measurements = [
      ...this.m_measurements,
      {
        id,
        startSnapshotId: snapshotId,
        endSnapshotId: snapshotId,
        startLocal: local,
        endLocal: local,
        rulerOffset: [0, 0, 0],
        isEdited: true,
        isOverEmptyArea: false,
      },
    ];
    this.onChanged.emit(this.m_measurements);
    return id;
  }

  updateMeasurement(
    id: string,
    pointType: MeasurementPointType,
    snapshotId: string | null,
    worldPos: Vec3,
  ): void {
    const current = this.find(id);
    if (!current || pointType === "pill") return;
    this.rememberPreEdit(current);

    const isValidPart = snapshotId !== null && snapshotId.length > 0;
    // Unity keeps the previous anchor when the pointer leaves geometry, so the
    // endpoint still tracks the ray but reverts cleanly on release.
    const anchorId =
      isValidPart
        ? snapshotId
        : pointType === "start"
          ? current.startSnapshotId
          : current.endSnapshotId;
    const local = this.toLocal(anchorId, worldPos);

    const next: Measurement =
      pointType === "start"
        ? { ...current, startSnapshotId: anchorId, startLocal: local, isEdited: true, isOverEmptyArea: !isValidPart }
        : { ...current, endSnapshotId: anchorId, endLocal: local, isEdited: true, isOverEmptyArea: !isValidPart };
    this.replace(next);
  }

  updateRulerOffset(id: string, initialLocalOffset: Vec3, worldOffset: Vec3): void {
    const current = this.find(id);
    if (!current) return;
    this.rememberPreEdit(current);

    // Unity: offset = initial + InverseTransformDirection(worldOffset). The web
    // measurement group is unrotated relative to the scene root, so the inverse
    // transform is the identity and the world delta adds directly.
    const raw: Vec3 = [
      initialLocalOffset[0] + worldOffset[0],
      initialLocalOffset[1] + worldOffset[1],
      initialLocalOffset[2] + worldOffset[2],
    ];
    const length = magnitude(raw);
    if (length < 1e-6) {
      this.replace({ ...current, rulerOffset: [0, 0, 0], isEdited: true });
      return;
    }

    // Project perpendicular to the line while preserving magnitude, verbatim:
    //   cross = offsetDir × lineDir ; offsetDir = lineDir × cross
    const offsetDir = scale(raw, 1 / length);
    const start = this.worldStart(current);
    const end = this.worldEnd(current);
    let lineDir: Vec3 = start && end ? sub(end, start) : [1, 0, 0];
    if (sqrMagnitude(lineDir) < 1e-5) lineDir = [1, 0, 0]; // Unity `Vector3.right`
    lineDir = normalize(lineDir);
    const perpendicular = cross(lineDir, cross(offsetDir, lineDir));

    this.replace({ ...current, rulerOffset: scale(perpendicular, length), isEdited: true });
  }

  finishNewMeasurement(id: string): void {
    const current = this.find(id);
    if (!current) return;
    const distance = this.getWorldDistance(id);
    // Unity discards a ruler that ended over empty space or is degenerate.
    if (current.isOverEmptyArea || distance === null || distance < K_MIN_WORLD_LENGTH) {
      this.deleteMeasurement(id);
      return;
    }
    this.m_preEdit.delete(id);
    this.replace({ ...current, isEdited: false, isOverEmptyArea: false });
  }

  finishEditedMeasurement(id: string, pointType: MeasurementPointType): void {
    const current = this.find(id);
    if (!current) return;
    const before = this.m_preEdit.get(id);
    this.m_preEdit.delete(id);

    if (current.isOverEmptyArea) {
      // Unity `ResetMeasurement` — put the dragged handle back.
      if (before) this.replace({ ...before, isEdited: false, isOverEmptyArea: false });
      return;
    }

    let rulerOffset = current.rulerOffset;
    if (pointType === "pill" && magnitude(rulerOffset) < K_SNAPBACK_THRESHOLD) {
      // Unity animates the snapback and persists zero; the web collapses
      // straight to zero (documented deviation — no tween layer for tools).
      rulerOffset = [0, 0, 0];
    }
    this.replace({ ...current, rulerOffset, isEdited: false, isOverEmptyArea: false });
  }

  deleteMeasurement(id: string): void {
    const before = this.m_measurements.length;
    this.m_measurements = this.m_measurements.filter((m) => m.id !== id);
    this.m_preEdit.delete(id);
    if (this.m_measurements.length !== before) this.onChanged.emit(this.m_measurements);
  }

  clear(): void {
    if (this.m_measurements.length === 0) return;
    this.m_measurements = [];
    this.m_preEdit.clear();
    this.onChanged.emit(this.m_measurements);
  }

  getWorldDistance(id: string): number | null {
    const m = this.find(id);
    if (!m) return null;
    const start = this.worldStart(m);
    const end = this.worldEnd(m);
    if (!start || !end) return null;
    return magnitude(sub(end, start));
  }

  getDistanceWithUnits(id: string): string {
    const distance = this.getWorldDistance(id);
    // Unity returns "" when an anchor can't resolve rather than showing 0.
    if (distance === null) return "";
    return formatDistance(distance, this.m_unit);
  }

  dispose(): void {
    this.m_offScene();
    this.m_offProject();
    this.m_measurements = [];
    this.m_preEdit.clear();
    this.m_host = null;
  }

  // -- internals ---------------------------------------------------------

  private find(id: string): Measurement | undefined {
    return this.m_measurements.find((m) => m.id === id);
  }

  private replace(next: Measurement): void {
    this.m_measurements = this.m_measurements.map((m) => (m.id === next.id ? next : m));
    this.onChanged.emit(this.m_measurements);
  }

  /** First mutation of a drag captures the revert target. */
  private rememberPreEdit(current: Measurement): void {
    if (!current.isEdited || !this.m_preEdit.has(current.id)) {
      if (!this.m_preEdit.has(current.id)) this.m_preEdit.set(current.id, current);
    }
  }

  private toLocal(snapshotId: string, world: Vec3): Vec3 {
    // No viewport (headless / tests): degrade to world-space storage.
    return this.m_host?.worldToLocal(snapshotId, world) ?? world;
  }

  private worldStart(m: Measurement): Vec3 | null {
    return this.m_host?.localToWorld(m.startSnapshotId, m.startLocal) ?? m.startLocal;
  }

  private worldEnd(m: Measurement): Vec3 | null {
    return this.m_host?.localToWorld(m.endSnapshotId, m.endLocal) ?? m.endLocal;
  }
}

// -- tiny vector helpers (the model layer must not import Three) ------------

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function scale(v: Vec3, k: number): Vec3 {
  return [v[0] * k, v[1] * k, v[2] * k];
}

function sqrMagnitude(v: Vec3): number {
  return v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
}

function magnitude(v: Vec3): number {
  return Math.sqrt(sqrMagnitude(v));
}

function normalize(v: Vec3): Vec3 {
  const len = magnitude(v);
  return len < 1e-9 ? [1, 0, 0] : scale(v, 1 / len);
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}
